<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import type { SuspendedDomain } from '@/types/compliance'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'SuspendedDomainsList' })

const { t } = useI18n()
const notifyStore = useNotifyStore()

const loading = ref(true)
const suspendedDomains = ref<SuspendedDomain[]>([])
const searchQuery = ref('')
const unsuspendingId = ref<number | null>(null)

// Fetch suspended domains
async function fetchSuspendedDomains() {
  loading.value = true

  try {
    const res = await useAxios<null, SuspendedDomain[]>(
      'GET',
      '/admin/domains/suspended',
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      suspendedDomains.value = res.data
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'SuspendedDomainsList' })
  } finally {
    loading.value = false
  }
}

// Filtered domains
const filteredDomains = computed(() => {
  if (!searchQuery.value.trim()) return suspendedDomains.value

  const query = searchQuery.value.toLowerCase()
  return suspendedDomains.value.filter(d =>
    d.domain_name.toLowerCase().includes(query) ||
    d.owner_email?.toLowerCase().includes(query)
  )
})

// Format date
function formatDate(dateString: string): string {
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm')
}

// Get suspension reason label
function getReasonLabel(reason: string): string {
  switch (reason) {
    case 'abuse': return 'Missbrauch'
    case 'nis2_violation': return 'NIS2 Verstoß'
    case 'legal': return 'Rechtlich'
    case 'manual': return 'Manuell'
    default: return reason
  }
}

// Unsuspend domain
async function unsuspendDomain(domain: SuspendedDomain) {
  unsuspendingId.value = domain.id

  try {
    const res = await useAxios<null, null>(
      'POST',
      `/admin/domain/${domain.id}/unsuspend`,
      true
    )

    if (res && res.status === STATUS_CODES.OK) {
      suspendedDomains.value = suspendedDomains.value.filter(d => d.id !== domain.id)
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.compliance.suspended.unsuspendSuccess'),
        message: domain.domain_name
      })
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'SuspendedDomainsList:unsuspend' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.admin.compliance.suspended.unsuspendError'),
      message: domain.domain_name
    })
  } finally {
    unsuspendingId.value = null
  }
}

onMounted(() => {
  fetchSuspendedDomains()
})
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.admin.compliance.suspended.title')"
    :subtitle="t('dashboard.admin.compliance.suspended.subtitle')"
    icon="mdi-domain-off"
    icon-color="orange"
    show-back
    :back-route="{ name: 'dashboardAdminCompliance' }"
  >
    <template #actions>
      <v-btn
        icon
        variant="text"
        :loading="loading"
        @click="fetchSuspendedDomains"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </template>

    <!-- Search Bar -->
    <div class="search-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="t('dashboard.admin.compliance.suspended.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredDomains.length === 0" class="empty-state">
      <v-icon icon="mdi-check-circle-outline" size="64" />
      <h3>{{ t('dashboard.admin.compliance.suspended.empty') }}</h3>
      <p>{{ t('dashboard.admin.compliance.suspended.emptyDescription') }}</p>
    </div>

    <!-- Suspended Domains Table -->
    <div v-else class="table-container">
      <table class="suspended-table">
        <thead>
          <tr>
            <th>{{ t('dashboard.admin.compliance.suspended.columns.domain') }}</th>
            <th>{{ t('dashboard.admin.compliance.suspended.columns.suspendedAt') }}</th>
            <th>{{ t('dashboard.admin.compliance.suspended.columns.reason') }}</th>
            <th>{{ t('dashboard.admin.compliance.suspended.columns.suspendedBy') }}</th>
            <th>{{ t('dashboard.admin.compliance.suspended.columns.owner') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="domain in filteredDomains"
            :key="domain.id"
            class="table-row"
          >
            <td class="domain-cell">
              <span class="domain-name">{{ domain.domain_name }}</span>
            </td>
            <td class="date-cell">{{ formatDate(domain.suspended_at) }}</td>
            <td class="reason-cell">
              <span class="reason-badge">{{ getReasonLabel(domain.reason) }}</span>
            </td>
            <td class="admin-cell">{{ domain.suspended_by }}</td>
            <td class="owner-cell">{{ domain.owner_email || '-' }}</td>
            <td class="action-cell">
              <v-btn
                color="success"
                variant="tonal"
                size="small"
                :loading="unsuspendingId === domain.id"
                @click="unsuspendDomain(domain)"
              >
                <v-icon icon="mdi-lock-open" class="me-1" size="16" />
                {{ t('dashboard.admin.compliance.suspended.unsuspend') }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Search Bar */
.search-bar {
  margin-bottom: 24px;
}

.search-input {
  max-width: 400px;
}

/* Loading/Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(76, 175, 80, 0.4);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin: 0;
  }
}

/* Table */
.table-container {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.suspended-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 14px 16px;
    text-align: left;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
}

.domain-cell .domain-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.date-cell,
.admin-cell,
.owner-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.reason-badge {
  display: inline-flex;
  padding: 4px 10px;
  background: rgba(255, 152, 0, 0.15);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #ffb74d;
}

.action-cell {
  text-align: right;
}

/* Light Theme */
.v-theme--lightTheme {
  .table-container {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .suspended-table th {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(15, 23, 42, 0.5);
  }

  .domain-cell .domain-name {
    color: rgba(15, 23, 42, 0.9);
  }

  .date-cell,
  .admin-cell,
  .owner-cell {
    color: rgba(15, 23, 42, 0.6);
  }
}

/* Mobile */
@media (max-width: 768px) {
  .suspended-domains-list {
    padding: 16px;
  }

  .search-input {
    max-width: 100%;
  }

  .table-container {
    overflow-x: auto;
  }

  .suspended-table {
    min-width: 700px;
  }
}
</style>
